import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@ui-nuvertos/shared';

export interface ClientSettlementMapping {
    client_id: number;
    credit_module_name: string;
    credit_module_id: number;
    debit_module_name: string;
    debit_module_id: number;
    mapped_amount: number;
    mapping_date: string;
}

export interface ClientBulkSettlementRequest {
    mappings: ClientSettlementMapping[];
}

export interface ClientBulkSettlementResponse {
    status: string;
    message: string;
    data: {
        successful: Array<{
            index: number;
            mapping_id: number;
            credit_module: string;
            debit_module: string;
            mapped_amount: number;
        }>;
        failed: Array<{
            index: number;
            error: string;
        }>;
    };
}

export interface ClientCreditModule {
    id: number;
    module_name: string;
    transaction_no?: string;
    reference_no?: string;
    date: string;
    due_date?: string;
    original_amount: number;
    unsettled_amount: number;
    partially_mapped_amount?: number;
    status: string;
    description?: string;
}

export interface ClientDebitModule {
    id: number;
    module_name: string;
    transaction_no?: string;
    reference_no?: string;
    date: string;
    amount: number;
    unsettled_amount: number;
    status: string;
    description?: string;
}

@Injectable({
    providedIn: 'root',
})
export class ClientSettlementService {
    private baseUrl = environment.accountingUrl;

    constructor(private http: HttpClient) { }

    /**
     * Get client credit modules (client_invoice, client_credit_note)
     * These are amounts owed BY the client
     */
    getClientCreditModules(
        clientId: number | string,
        filters?: {
            subsidiary_id?: number;
            start_date?: string;
            end_date?: string;
            status?: string;
        }
    ): Observable<any> {
        let params = new HttpParams();
        if (filters?.subsidiary_id) {
            params = params.set('subsidiary_id', filters.subsidiary_id.toString());
        }
        if (filters?.start_date) {
            params = params.set('start_date', filters.start_date);
        }
        if (filters?.end_date) {
            params = params.set('end_date', filters.end_date);
        }
        if (filters?.status) {
            params = params.set('status', filters.status);
        }

        return this.http.get(
            `${this.baseUrl}/api/v1/client-settlement/credit-module/${clientId}`,
            { params }
        );
    }

    /**
     * Get client debit modules (client_payment, jv_debit_lines)
     * These are amounts that reduce what the client owes
     */
    getClientDebitModules(
        clientId: number | string,
        filters?: {
            subsidiary_id?: number;
            start_date?: string;
            end_date?: string;
            status?: string;
        }
    ): Observable<any> {
        let params = new HttpParams();
        if (filters?.subsidiary_id) {
            params = params.set('subsidiary_id', filters.subsidiary_id.toString());
        }
        if (filters?.start_date) {
            params = params.set('start_date', filters.start_date);
        }
        if (filters?.end_date) {
            params = params.set('end_date', filters.end_date);
        }
        if (filters?.status) {
            params = params.set('status', filters.status);
        }

        return this.http.get(
            `${this.baseUrl}/api/v1/client-settlement/debit-module/${clientId}`,
            { params }
        );
    }

    /**
     * Create bulk settlement mappings
     */
    createBulkSettlementMappings(
        request: ClientBulkSettlementRequest
    ): Observable<ClientBulkSettlementResponse> {
        return this.http.post<ClientBulkSettlementResponse>(
            `${this.baseUrl}/api/v1/client-settlement/mappings/bulk`,
            request
        );
    }

    /**
     * Get existing settlement mappings for a client
     */
    getSettlementMappings(
        clientId: number | string,
        filters?: {
            start_date?: string;
            end_date?: string;
        }
    ): Observable<any> {
        let params = new HttpParams();
        if (filters?.start_date) {
            params = params.set('start_date', filters.start_date);
        }
        if (filters?.end_date) {
            params = params.set('end_date', filters.end_date);
        }

        return this.http.get(
            `${this.baseUrl}/api/v1/client-settlement/${clientId}/mappings`,
            { params }
        );
    }

    /**
     * Delete a settlement mapping
     */
    deleteSettlementMapping(mappingId: number | string): Observable<any> {
        return this.http.delete(
            `${this.baseUrl}/api/v1/client-settlement/mappings/${mappingId}`
        );
    }
}
